import React, { useState, useEffect, useRef } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Helmet from 'react-helmet';
// Interval for automatic image change (in milliseconds)
const AUTO_SLIDE_INTERVAL = 3000; // Change image every 3 seconds automatically

const ProjectCard = ({ project, onEdit, onDelete, onViewDetails }) => {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const intervalRef = useRef(null); // Ref to hold the interval ID

  // Check if project.imageUrl exists and has more than one image object
  const hasMultipleImages = project.imageUrl && project.imageUrl.length > 1;

  // Function to advance the slide
  const goToNextSlide = () => {
    setCurrentSlideIndex((prevIndex) =>
      (prevIndex + 1) % project.imageUrl.length
    );
  };

  // Start automatic slideshow when component mounts if there are multiple images
  useEffect(() => {
    if (hasMultipleImages) {
      // Clear any existing interval to prevent multiple intervals running (important on re-render)
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      intervalRef.current = setInterval(goToNextSlide, AUTO_SLIDE_INTERVAL);
    }

    // Cleanup function to clear the interval when the component unmounts
    // or when hasMultipleImages changes (e.g., project data updates)
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [hasMultipleImages, project.imageUrl.length]); // Re-run if image array or its length changes

  // Access the current image object based on currentSlideIndex
  const currentImageObject = project.imageUrl && project.imageUrl.length > 0
    ? project.imageUrl[currentSlideIndex]
    : null;

  const displayImageUrl = currentImageObject?.url || ''; // Safely get the URL
  const displayImageDescription = currentImageObject?.description || ''; // Safely get the description (still used for alt text/modal)

  const truncateDescription = (desc, limit = 150) =>
    desc.length > limit ? `${desc.slice(0, limit)}...` : desc;

  const shortDescription = truncateDescription(project.description);


  return (
    <div
      className="projectCard"
    >
      {displayImageUrl ? ( // Only render image container if there's an image URL
        <div className="projectCardImageContainer" onClick={() => onViewDetails(project)}>
          <img
            src={displayImageUrl}
            alt={project.title + (displayImageDescription ? ` - ${displayImageDescription}` : '')} // Use image description in alt
            className="projectCardImage"
          />
          {hasMultipleImages && (
            <div className="projectCard-image-overlay">
              {/* Reverted to a simple "Preview" text */}
              <span className="slide-indicator">Preview</span>
            </div>
          )}
        </div>
      ) : (
        <div className="projectCardImageContainer no-image">
            <span className="no-image-text">No Image</span>
        </div>
      )}
      <h3 className="projectCardTitle" onClick={() => onViewDetails(project)}>
        {project.title}
      </h3>
      <p className="projectCardDescription">{shortDescription}</p>
      {project.description.length > 150 && (
        <button
          className="projectCard-viewMoreButton"
          onClick={() => onViewDetails(project)}
        >
          View More
        </button>
      )}
      <div className="projectCard-techStackDiv">
        <strong>Tech Stack:</strong>
        <div className="projectCard-techStackCapsules">
          {project.techStack.map((tech, i) => (
            <span key={i} className="tech-capsule">{tech}</span>
          ))}
        </div>
      </div>
      <div className="dashboard-project-card-footer">
        <div className="dashboard-project-links-div">
          {project.githubUrl && (
            <a href={project.githubUrl} target="_blank" rel="noopener noreferrer" className="dashboard-project-card-link">
              GitHub
            </a>
          )}
          {project.liveUrl && (
            <a href={project.liveUrl} target="_blank" rel="noopener noreferrer" className="dashboard-project-card-link">
              Live
            </a>
          )}
        </div>
        <div className="dashboard-project-buttons-div">
          <button onClick={() => onEdit(project)} title="Edit Project" className="edit-btn">
            <EditIcon fontSize="inherit" />
          </button>
          <button onClick={() => onDelete(project)} title="Delete Project" className="delete-btn">
            <DeleteIcon fontSize="inherit" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProjectCard;